import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, message, Skeleton, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import MonthCard from './MonthCard';
import MonthDrawer from './MonthDrawer';
import { type ClubMonth, type ClubScheduleData, nextMonthRange, type ScheduleClub } from './types';

const { Title, Text } = Typography;

interface Props {
  club: ScheduleClub;
  canEdit: boolean;
}

// A club's schedule on the club registration page (CONTEXT.md: Club month,
// Session): its months, each with the dates it meets.
export default function ClubScheduleSection({ club, canEdit }: Props) {
  const { runQuery } = useScreen();
  const [data, setData] = useState<ClubScheduleData | null>(null);
  const [draft, setDraft] = useState<{ month: ClubMonth | null; range: [Dayjs, Dayjs] } | null>(
    null,
  );

  const load = useCallback(() => {
    runQuery<ClubScheduleData>('club_schedule', { clubId: club.id })
      .then(([d]) => setData(d))
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery, club.id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const holidays = useMemo(
    () => new Map((data?.holidays ?? []).map((h) => [h.date, h.title])),
    [data],
  );
  const totalSessions = data?.months.reduce((sum, m) => sum + m.dates.length, 0) ?? 0;
  const addMonth = () =>
    data && setDraft({ month: null, range: nextMonthRange(club, data.months) });

  return (
    <Card style={{ marginBottom: 16 }}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Дугуйлангийн хуваарь
          </Title>
          {data && data.months.length > 0 && (
            <Text type="secondary" className="text-xs">
              {data.months.length} сар · {totalSessions} хичээллэх өдөр
            </Text>
          )}
        </div>
        {canEdit && (
          <Button icon={<PlusOutlined />} disabled={!data} onClick={addMonth}>
            Сар нэмэх
          </Button>
        )}
      </div>

      {!data ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : data.months.length === 0 ? (
        <Empty description="Хуваарь оруулаагүй байна" />
      ) : (
        <div className="flex flex-col gap-3">
          {data.months.map((month) => (
            <MonthCard
              key={month.id}
              club={club}
              month={month}
              holidays={holidays}
              canEdit={canEdit}
              onEdit={() =>
                setDraft({ month, range: [dayjs(month.start_date), dayjs(month.end_date)] })
              }
              onChanged={load}
            />
          ))}
        </div>
      )}

      <MonthDrawer
        clubId={club.id}
        draft={draft}
        onClose={() => setDraft(null)}
        onSaved={() => {
          setDraft(null);
          load();
        }}
      />
    </Card>
  );
}
